import type { ComponentType } from 'react';
import type { Amenity, Fee, Floorplan, PetPolicy, PhotoGallery, SpecialOffer, Unit, VirtualTour } from '@vibesites/schema';
import type { CollectionBindingState } from '@vibesites/runtime/react';
import { isActive } from '@vibesites/schema';
import { parseBedroomCount } from '../../shared/bedroom-options';
import { amount, count, formatDay, formatDollars, formatNumber, formatNumberRange, formatRange, isoDate, span, text } from './wire';

/** A calendar date as the wire sends it, YYYY-MM-DD. Minted only by wire.isoDate. */
export type IsoDate = string & { readonly __brand: 'IsoDate' };

/** Inclusive range of positive numbers with min <= max. A single value has min === max. */
export interface Range {
  readonly min: number;
  readonly max: number;
}

export type FloorplansLayout = 'cards' | 'rows';

export interface FloorplansViewProps {
  /** Null when the title token is empty. */
  readonly header: FloorplansHeader | null;
  readonly layout: LayoutSwitch;
  readonly content: FloorplansContent;
}

/** The visitor's pick between rows and cards. The catalog block starts on cards below 768px and rows above. Not a theme token. */
export interface LayoutSwitch {
  readonly selected: FloorplansLayout;
  readonly select: (layout: FloorplansLayout) => void;
}

/** Editable text owned by the block. The view picks the tag and classes. The block keeps the token wiring. */
export type EditableText = ComponentType<{ readonly as?: 'h2' | 'h3' | 'p' | 'span'; readonly className?: string }>;

export interface FloorplansHeader {
  readonly Title: EditableText;
  readonly Subtitle: EditableText | null;
}

export type FloorplansContent =
  | {
      readonly status: 'loading';
      /** Sorting needs no plans, so the visitor can pick one before they load. Omitted when the caller passes no sort selection. */
      readonly sort?: PlanSorter;
      /** The property's own fees and pets. Null until it has some. A list every plan sends only shows once the plans load. */
      readonly terms: PropertyTerms | null;
      readonly contact: Contact | null;
    }
  | { readonly status: 'failed'; readonly contact: Contact | null }
  | { readonly status: 'empty'; readonly contact: Contact | null }
  | {
      readonly status: 'ready';
      /** After the bedroom filter and `refine`, in the visitor's sort order. Empty only when `refine.active` is above 0 and nothing matches. */
      readonly plans: readonly (Floorplan & Display)[];
      /** The same plans grouped by bedroom count, fewest beds first, plans without a count last. */
      readonly groups: readonly PlanGroup[];
      /** Omitted when the caller passes no bed selection. Null when the plans span fewer than two bedroom counts. */
      readonly filter?: BedroomFilter | null;
      /** Omitted when the caller passes no sort selection. */
      readonly sort?: PlanSorter;
      /** Omitted when the caller passes no filter selection. Null when no plan has a rent span, a size span, or a future move-in date. */
      readonly refine?: Refinement | null;
      /** Property-wide fees and pets. Fees are the property's, else the list every plan sends. */
      readonly terms: PropertyTerms | null;
      /** The fees the feed attaches to the property itself. `feesFor` lists them under a home's own. */
      readonly propertyFees: readonly Fee[];
      readonly notice: Notice | null;
      /** The `contact` slot group's url, else tel: property_phone. For a closing prompt under the plans. */
      readonly contact: Contact | null;
      /** The date the labels were computed against. A view formats specials relative to it. */
      readonly today: IsoDate;
    };

export interface PlanGroup {
  /** Null holds the plans without a bedroom count. */
  readonly beds: number | null;
  /** "Studio", "1 bedroom", "2 bedrooms", "Other plans" */
  readonly label: string;
  /** "3 plans, from $1,845". The price part is left out when no plan in the group is priced. */
  readonly summary: string;
  readonly plans: readonly (Floorplan & Display)[];
}

/** Set when the fetch failed and the rows came from earlier in the session or from the bake. */
export interface Notice {
  readonly kind: 'last-known';
  readonly label: string;
}

export interface Contact {
  readonly href: string;
  readonly label: string;
  /** The link's `_target` is `_blank`. */
  readonly newTab: boolean;
}

/** The apply link. `href` is null when the property sends none. A unit's own apply url still gets the label and tab. */
export interface ApplyLink {
  readonly href: string | null;
  readonly label: string;
  readonly newTab: boolean;
}

/** A wire tour plus its checked https link and label. */
export type Tour = VirtualTour & { readonly href: string; readonly label: string };

/** A wire pet policy plus its label. */
export type LabeledPet = PetPolicy & { readonly label: string };

/** Computed additions to each schema Floorplan. Wire fields are not restated. */
export type Display = {
  /** Null when floorplan_beds_min does not parse. */
  readonly beds: number | null;
  /** Null when floorplan_baths_min is missing, 0, or the "false" sentinel. */
  readonly baths: number | null;
  /** floorplan_beds_max and floorplan_baths_max when above the min, for "1–2 beds". Null otherwise. */
  readonly bedsMax: number | null;
  readonly bathsMax: number | null;
  /** floorplan_description, when the plan has one. */
  readonly description: string | null;
  /** The plan's own photos, without a copy of the floor plan image. */
  readonly photos: readonly PlanPhoto[];
  /** The plan's own amenities. Homes carry theirs on `ListedUnit.amenities`. */
  readonly amenities: readonly AmenityLine[];
  /** The plan's own specials that are neither disabled nor ended. Every listed home gets them. */
  readonly planSpecials: readonly SpecialOffer[];
  /** Listed homes with a special running today, their own or the plan's. */
  readonly homesWithSpecials: number;
  /** Plan sqft, else the span of listed-unit sqft. Null when neither parses above 0. */
  readonly sqft: Range | null;
  readonly rent: Rent;
  readonly listed: readonly ListedUnit[];
  readonly availability: Availability;
  readonly action: PlanAction;
  /** "Studio, 1 bath, 568 sq ft". Empty only when beds, baths, and sqft are all missing. */
  readonly factsLabel: string;
  readonly unitColumns: UnitColumns;
  /** "$300 deposit", "$350 to $500 deposit by unit, plan lists $300". */
  readonly deposit: string | null;
  /** The first https tour: the plan's own, else any unit's. */
  readonly tour: Tour | null;
  /** The tours no listed home carries: the plan's own, then those of units that are not listed. Listed homes carry theirs on `ListedUnit.tours`. */
  readonly tours: readonly Tour[];
  readonly pets: readonly LabeledPet[];
  /** The plan's and listed homes' specials running today, as sent, the first of each title kept. The chip shows the only one, or a count. */
  readonly runningSpecials: readonly SpecialOffer[];
};

/** A plan photo as the wire sends it, with its checked url. */
export interface PlanPhoto {
  readonly url: string;
  readonly alt: string;
  readonly title: string | null;
  readonly description: string | null;
  readonly media: { readonly width?: number | null; readonly height?: number | null } | null;
}

/** One amenity as the feed sends it. `amount` is shown as sent and never added to rent. */
export interface AmenityLine {
  readonly title: string;
  readonly description: string | null;
  readonly amount: string | null;
  readonly featured: boolean;
}

/** The plan dialog's amenity list, grouped by where each amenity lives. Empty groups are left out. */
export type AmenityGroup =
  | { readonly kind: 'home'; readonly home: ListedUnit; readonly items: readonly AmenityLine[] }
  | { readonly kind: 'plan'; readonly items: readonly AmenityLine[] }
  | { readonly kind: 'others'; readonly items: readonly (AmenityLine & { readonly homes: readonly ListedUnit[] })[] };

export type Rent =
  /** listed is the listed units. plan is the plan's own price. all-units is every priced unit, listed or not. */
  | { readonly kind: 'priced'; readonly basis: 'listed' | 'plan' | 'all-units'; readonly range: Range; readonly label: string }
  | { readonly kind: 'contact'; readonly label: string };

export type Availability =
  /** now counts units available today. later counts the rest. next is the earliest future date. */
  | { readonly kind: 'listed'; readonly now: number; readonly later: number; readonly next: IsoDate | null; readonly label: string }
  /** A plan without units that reports a count above 0. */
  | { readonly kind: 'reported'; readonly count: number; readonly label: string }
  /** Units exist and none is listed, or a plan without units reports "0". */
  | { readonly kind: 'waitlist'; readonly label: string }
  /** No units and no count. */
  | { readonly kind: 'unknown'; readonly label: string };

export type PlanAction =
  | { readonly kind: 'apply'; readonly href: string; readonly label: string; readonly newTab: boolean }
  | { readonly kind: 'contact'; readonly href: string; readonly label: string; readonly newTab: boolean }
  | { readonly kind: 'none' };

export type MoveIn = 'now' | 'undated' | IsoDate;

/** A listed wire unit plus the labels that depend on today's date. `unit` is the wire unit with `floor` and `apply_url` cleaned of the "false" sentinel. `specials` are the unit's own offers that are neither disabled nor ended. */
export interface ListedUnit {
  readonly unit: Unit;
  readonly moveIn: MoveIn;
  /** "Now", "Dec 10", or "Ask for date" */
  readonly moveInLabel: string;
  /** "$1,319/mo", "$1,399 to $1,505/mo", or "Contact for pricing" when the unit has no price above 0. */
  readonly rentLabel: string;
  /** Null when sqft is missing, 0, or the "false" sentinel. */
  readonly sqftLabel: string | null;
  readonly depositLabel: string | null;
  readonly specials: readonly SpecialOffer[];
  /** The home's own amenities, titled and deduped. Feeds that copy the plan's onto every home repeat them here. */
  readonly amenities: readonly AmenityLine[];
  /** The home's own photos, without a copy of the drawing or of a plan photo. Homes may share a photo. */
  readonly photos: readonly PlanPhoto[];
  readonly tour: Tour | null;
  /** The home's own tours, without any the plan itself sends. */
  readonly tours: readonly Tour[];
  /** "Building N, floor 14", "Floor 3", or "Building E". Null when the unit sends neither. */
  readonly location: string | null;
}

/** `deposit` is set only when listed units differ. A shared deposit is `Display.deposit`. */
export type UnitColumns = { readonly [K in 'sqft' | 'location' | 'deposit' | 'special' | 'tour' | 'apply']: boolean };

export interface PropertyTerms {
  readonly fees: readonly Fee[];
  readonly pets: readonly LabeledPet[];
}

/** Where the feed attached a fee. A home's list never absorbs another level's. */
export type FeeSource = 'home' | 'plan' | 'property';

export interface ListedFee {
  readonly fee: Fee;
  readonly from: FeeSource;
}

/** Split on `is_required` only. Anything not `true` is optional. */
export interface FeeList {
  readonly required: readonly ListedFee[];
  readonly optional: readonly ListedFee[];
  readonly count: number;
}

/** A bedroom count, or null for every plan. */
export type BedChoice = number | null;

export interface BedroomFilter {
  /** beds null is "All". */
  readonly options: readonly { readonly beds: BedChoice; readonly label: string; readonly count: number }[];
  readonly selected: BedChoice;
  readonly select: (beds: BedChoice) => void;
}

/** The visitor's narrowing past bedrooms. Null leaves that one open. */
export interface PlanFilters {
  /** Monthly rent. A home matches when its rent range overlaps this one. */
  readonly rent: Range | null;
  /** A home without its own size uses the plan's. */
  readonly sqft: Range | null;
  /** Homes that can move in on or before this date. A home without a date does not match. */
  readonly moveInBy: IsoDate | null;
  /** Plans with exactly this many baths. */
  readonly baths: number | null;
  /** Homes whose feed floor is exactly this text. A home without a floor does not match. */
  readonly floor: string | null;
}

/** A slider's ends, rounded out to `step`. */
export interface FilterRange extends Range {
  readonly step: number;
}

/**
 * Filters over the listed homes. A plan stays when one of its listed homes matches every filter, and then lists only
 * those homes, with its rent, availability, and special computed from them. A plan with no listed homes matches on
 * its own rent and size, and never on a move-in date. Bounds come from every plan, before the bedroom filter.
 */
export interface Refinement {
  /** Null when every priced plan costs the same. */
  readonly rent: FilterRange | null;
  readonly sqft: FilterRange | null;
  /** Today through the latest dated listed home. Null when no listed home has a future date. */
  readonly moveIn: { readonly earliest: IsoDate; readonly latest: IsoDate } | null;
  /** Each plan's bath count, low to high. Null with fewer than two. */
  readonly baths: readonly number[] | null;
  /**
   * The floors the feed sends on listed homes, as sent, in natural order ("2" before "10"). Null unless every listed
   * home has one and there are at least two. Never read as numbers: feeds send "Second Floor", "T", and "1.5".
   */
  readonly floors: readonly string[] | null;
  /** The caller's filters, without the ones whose bounds are null. */
  readonly selected: PlanFilters;
  /** How many of `selected` are set. */
  readonly active: number;
  readonly select: (filters: PlanFilters) => void;
}

export const NO_FILTERS: PlanFilters = { rent: null, sqft: null, moveInBy: null, baths: null, floor: null };

interface FilterSelection {
  readonly selected: PlanFilters;
  readonly select: (filters: PlanFilters) => void;
}

const RENT_STEP = 50;
const SQFT_STEP = 25;

interface Offer {
  readonly unit: Unit;
  readonly moveIn: MoveIn;
}

export interface ModelContext {
  readonly today: IsoDate;
  /** The `apply` slot group. */
  readonly apply: ApplyLink;
  /** The `contact` slot group's url, else tel: property_phone. */
  readonly contact: Contact | null;
  /** `useFees()`. Fees are a live collection, so the bake strips them and static HTML has none. */
  readonly propertyFees: readonly Fee[];
  /** Baked `property.pet_policies`. */
  readonly propertyPets: readonly PetPolicy[];
}

type FloorplanBinding = Pick<CollectionBindingState<Floorplan>, 'status' | 'items'>;

export type PlanSort = 'available' | 'price-low' | 'price-high';

export interface PlanSorter {
  readonly options: readonly { readonly value: PlanSort; readonly label: string }[];
  readonly selected: PlanSort;
  readonly select: (sort: PlanSort) => void;
}

interface SortSelection {
  readonly selected: PlanSort;
  readonly select: (sort: PlanSort) => void;
}

type PlanOrder = (a: Floorplan & Display, b: Floorplan & Display) => number;

/** Soonest move-in puts the earliest move-in first. Price sorts put unpriced plans last either way. Ties keep binding order. */
const SORTS: Record<PlanSort, { readonly label: string; readonly order: PlanOrder }> = {
  available: { label: 'Soonest move-in', order: (a, b) => soonest(a) - soonest(b) },
  'price-low': { label: 'Lowest rent', order: (a, b) => (lowRent(a) ?? Infinity) - (lowRent(b) ?? Infinity) },
  'price-high': { label: 'Highest rent', order: (a, b) => (lowRent(b) ?? -Infinity) - (lowRent(a) ?? -Infinity) },
};

/** Days from today to the first listed move-in. Undated and reported homes follow dated ones, and plans with none come last. */
function soonest(plan: Floorplan & Display): number {
  const first = plan.listed[0]?.moveIn;
  if (first === 'now') return 0;
  if (first != null && first !== 'undated') return Date.parse(first) / 86_400_000;
  return hasHomes(plan) ? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
}

function lowRent(plan: Floorplan & Display): number | null {
  return plan.rent.kind === 'priced' ? plan.rent.range.min : null;
}

interface BedSelection {
  readonly selected: BedChoice;
  readonly select: (beds: BedChoice) => void;
}

const CONTACT_RENT = 'Contact for pricing';
const LAST_KNOWN = 'Showing last known floor plans';

/** The schema floorplans, each with the labels the rules compute. Pure. */
export function prepareFloorplans(floorplans: readonly Floorplan[], ctx: ModelContext): Array<Floorplan & Display> {
  return floorplans.map((floorplan) => {
    const offered = listUnits(floorplan, ctx.today);
    const availability = toAvailability(floorplan, offered, ctx.today);
    const drawing = text(floorplan.floorplan_image_url);
    const seen = new Set<string>(drawing ? [drawing] : []);
    const photos = galleryPhotos(floorplan.photo_gallery, `${text(floorplan.floorplan_name) ?? 'Floor plan'} photo`, seen);
    const planOwn = toursOn(floorplan);
    const ownHrefs = new Set(planOwn.map((tour) => tour.href));
    const listed = offered.map((item) => toListedUnit(item, ctx.today, seen, ownHrefs));
    const onHomes = new Set(listed.flatMap((home) => home.tours.map((tour) => tour.href)));
    const unlisted = (floorplan.units ?? []).filter((unit) => !offered.some((item) => item.unit === unit));
    const beds = bedCount(floorplan);
    const baths = amount(floorplan.floorplan_baths_min);
    const sqft = sqftOf(floorplan, offered);
    const bedsMax = above(parseBedroomCount(floorplan.floorplan_beds_max), beds);
    const bathsMax = above(amount(floorplan.floorplan_baths_max), baths);
    const planSpecials = (floorplan.special_offers ?? []).filter((offer) => isActive(offer, ctx.today));
    return {
      ...floorplan,
      floorplan_name: planName(floorplan.floorplan_name),
      floorplan_image_url: text(floorplan.floorplan_image_url),
      floorplan_beds_min: text(floorplan.floorplan_beds_min),
      floorplan_beds_max: text(floorplan.floorplan_beds_max),
      floorplan_baths_min: text(floorplan.floorplan_baths_min),
      floorplan_baths_max: text(floorplan.floorplan_baths_max),
      floorplan_sqft_min: text(floorplan.floorplan_sqft_min),
      floorplan_sqft_max: text(floorplan.floorplan_sqft_max),
      floorplan_price_min: text(floorplan.floorplan_price_min),
      floorplan_price_max: text(floorplan.floorplan_price_max),
      beds,
      baths,
      bedsMax,
      bathsMax,
      description: text(floorplan.floorplan_description),
      photos,
      amenities: amenityLines(floorplan.amenities),
      planSpecials,
      homesWithSpecials: listed.filter((row) => planSpecials.some((offer) => running(offer, ctx.today)) || row.specials.some((offer) => running(offer, ctx.today))).length,
      sqft,
      factsLabel: factsLabel(beds, baths, sqft, bedsMax, bathsMax),
      rent: toRent(floorplan, offered),
      availability,
      action: toAction(offered, availability, ctx),
      listed,
      unitColumns: unitColumns(listed),
      deposit: toDeposit(floorplan, offered),
      tour: distinctTours([...planOwn, ...(floorplan.units ?? []).flatMap((unit) => toursOn(unit))])[0] ?? null,
      tours: distinctTours([...planOwn, ...unlisted.flatMap((unit) => toursOn(unit)).filter((tour) => !onHomes.has(tour.href))]),
      pets: toPetTerms(floorplan.pet_policies ?? []),
      runningSpecials: distinctSpecials([...planSpecials, ...listed.flatMap((row) => row.specials)].filter((offer) => running(offer, ctx.today))),
    };
  });
}

/** Feeds send "1x1-745- Classic" and non-breaking hyphens. Codes like "2X2-947" stay as sent. */
function planName(value: unknown): string | null {
  const raw = text(value);
  if (!raw) return null;
  return raw.replace(/\u2011/g, '-').replace(/(\S)-\s+/g, '$1 ').replace(/(\S)-$/, '$1').trim() || raw;
}

/** Started on or before today. A special that starts later still lists, with its start date, but isn't counted as running. */
function running(offer: SpecialOffer, today: IsoDate): boolean {
  const startsOn = isoDate(offer.start_date);
  return startsOn == null || startsOn <= today;
}

/** A higher max than the min, else null. */
function above(max: number | null, min: number | null): number | null {
  return max != null && min != null && max > min ? max : null;
}

function distinctSpecials(offers: readonly SpecialOffer[]): SpecialOffer[] {
  const seen = new Set<string>();
  return offers.filter((offer) => {
    const key = text(offer.title) ?? text(offer.description);
    return key != null && !seen.has(key) && Boolean(seen.add(key));
  });
}

export function toContent(
  state: FloorplanBinding,
  ctx: ModelContext,
  beds?: BedSelection,
  sort?: SortSelection,
  filters?: FilterSelection,
): FloorplansContent {
  const items = state.items;
  switch (state.status) {
    case 'loading':
      return items.length ? ready(items, ctx, beds, sort, filters, null) : loading(ctx, sort);
    case 'failed':
      return items.length
        ? ready(items, ctx, beds, sort, filters, { kind: 'last-known', label: LAST_KNOWN })
        : { status: 'failed', contact: ctx.contact };
    case 'ready':
    case 'static':
      return items.length ? ready(items, ctx, beds, sort, filters, null) : { status: 'empty', contact: ctx.contact };
    default: {
      const unhandled: never = state.status;
      return unhandled;
    }
  }
}

function loading(ctx: ModelContext, sort: SortSelection | undefined): FloorplansContent {
  const pets = toPetTerms(ctx.propertyPets);
  return {
    status: 'loading',
    ...(sort ? { sort: sorter(sort) } : {}),
    terms: ctx.propertyFees.length || pets.length ? { fees: ctx.propertyFees, pets } : null,
    contact: ctx.contact,
  };
}

function sorter(sort: SortSelection): PlanSorter {
  return {
    options: (Object.keys(SORTS) as PlanSort[]).map((value) => ({ value, label: SORTS[value].label })),
    selected: sort.selected,
    select: sort.select,
  };
}

function ready(
  rows: readonly Floorplan[],
  ctx: ModelContext,
  beds: BedSelection | undefined,
  sort: SortSelection | undefined,
  filters: FilterSelection | undefined,
  notice: Notice | null,
): FloorplansContent {
  const cards = prepareFloorplans(rows, ctx);
  const refine = filters ? refinementOf(cards, ctx.today, filters) : null;
  const kept = refine?.active ? narrow(rows, cards, refine.selected, ctx) : cards;
  /** A bed count no plan has is a stale pick. It shows every plan. */
  const pick = beds?.selected ?? null;
  const selected = cards.some((card) => bedsOf(card).includes(pick ?? -1)) ? pick : null;
  const visible = selected == null ? kept : kept.filter((card) => bedsOf(card).includes(selected));
  const plans = sort ? [...visible].sort(SORTS[sort.selected].order) : visible;
  const fees = ctx.propertyFees.length ? ctx.propertyFees : sharedFees(cards) ?? [];
  const pets = toPetTerms(ctx.propertyPets);
  const terms = fees.length || pets.length ? { fees, pets } : null;
  return {
    status: 'ready',
    plans,
    groups: toGroups(plans),
    ...(beds ? { filter: bedroomFilter(cards, { ...beds, selected }) } : {}),
    ...(sort ? { sort: sorter(sort) } : {}),
    ...(filters ? { refine } : {}),
    terms,
    propertyFees: ctx.propertyFees,
    notice,
    contact: ctx.contact,
    today: ctx.today,
  };
}

function refinementOf(cards: readonly (Floorplan & Display)[], today: IsoDate, filters: FilterSelection): Refinement | null {
  /** Like the group summary, all-units rent is left out. It reads old prices on units that are not listed. */
  const rent = sliderRange(cards.flatMap((card) => (card.rent.kind === 'priced' && card.rent.basis !== 'all-units' ? [card.rent.range] : [])), RENT_STEP);
  const sqft = sliderRange(cards.flatMap((card) => (card.sqft ? [card.sqft] : [])), SQFT_STEP);
  const dates = cards.flatMap((card) => card.listed.flatMap((row) => (row.moveIn === 'now' || row.moveIn === 'undated' ? [] : [row.moveIn])));
  const latest = dates.sort()[dates.length - 1];
  const moveIn = latest ? { earliest: today, latest } : null;
  const bathCounts = [...new Set(cards.flatMap((card) => (card.baths == null ? [] : [card.baths])))].sort((a, b) => a - b);
  const baths = bathCounts.length > 1 ? bathCounts : null;
  const floors = floorsOf(cards);
  if (!rent && !sqft && !moveIn && !baths && !floors) return null;
  const selected: PlanFilters = {
    rent: rent ? filters.selected.rent : null,
    sqft: sqft ? filters.selected.sqft : null,
    moveInBy: moveIn ? filters.selected.moveInBy : null,
    baths: baths?.includes(filters.selected.baths ?? NaN) ? filters.selected.baths : null,
    floor: floors?.includes(filters.selected.floor ?? '') ? filters.selected.floor : null,
  };
  const active = Object.values(selected).filter((value) => value != null).length;
  return { rent, sqft, moveIn, baths, floors, selected, active, select: filters.select };
}

function floorsOf(cards: readonly (Floorplan & Display)[]): readonly string[] | null {
  const homes = cards.flatMap((card) => card.listed);
  if (!homes.length || homes.some((row) => row.unit.floor == null)) return null;
  const floors = [...new Set(homes.map((row) => String(row.unit.floor)))];
  return floors.length > 1 ? floors.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })) : null;
}

function sliderRange(ranges: readonly Range[], step: number): FilterRange | null {
  if (!ranges.length) return null;
  const { min, max } = enclose(ranges);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  return hi > lo ? { min: lo, max: hi, step } : null;
}

/** Each plan with a matching listed home, rebuilt from those homes. Unlisted units stay, so a plan's waitlist reads the same. */
function narrow(rows: readonly Floorplan[], cards: readonly (Floorplan & Display)[], filters: PlanFilters, ctx: ModelContext): (Floorplan & Display)[] {
  return cards.flatMap((card, index) => {
    if (filters.baths != null && card.baths !== filters.baths) return [];
    if (!card.listed.length) return planMatches(card, filters) ? [card] : [];
    const units = rows[index].units ?? [];
    const keep = units.filter((unit) => {
      const moveIn = moveInOf(unit, ctx.today);
      return moveIn == null || unitMatches(unit, moveIn, card.sqft, filters);
    });
    if (!keep.some((unit) => moveInOf(unit, ctx.today) != null)) return [];
    return keep.length === units.length ? [card] : prepareFloorplans([{ ...rows[index], units: keep }], ctx);
  });
}

function unitMatches(unit: Unit, moveIn: MoveIn, planSqft: Range | null, filters: PlanFilters): boolean {
  if (filters.rent) {
    const rent = rangeOf(unit);
    if (!rent || !overlaps(rent, filters.rent)) return false;
  }
  if (filters.sqft) {
    const size = amount(unit.sqft);
    const sqft = size == null ? planSqft : { min: size, max: size };
    if (!sqft || !overlaps(sqft, filters.sqft)) return false;
  }
  if (filters.moveInBy) {
    if (moveIn === 'undated') return false;
    if (moveIn !== 'now' && moveIn > filters.moveInBy) return false;
  }
  if (filters.floor != null && text(unit.floor) !== filters.floor) return false;
  return true;
}

function planMatches(card: Floorplan & Display, filters: PlanFilters): boolean {
  if (filters.moveInBy || filters.floor != null) return false;
  if (filters.rent && (card.rent.kind !== 'priced' || !overlaps(card.rent.range, filters.rent))) return false;
  if (filters.sqft && (!card.sqft || !overlaps(card.sqft, filters.sqft))) return false;
  return true;
}

function overlaps(a: Range, b: Range): boolean {
  return a.min <= b.max && a.max >= b.min;
}

/**
 * Every fee the feed attaches to this home, its plan, and the property, in that order and as sent. Levels are never
 * merged or deduped: a provider that repeats a fee at two levels shows it twice, each tagged with where it came from.
 */
export function feesFor(plan: Floorplan, home: Unit | null, propertyFees: readonly Fee[]): FeeList {
  const listed: ListedFee[] = [
    ...(home?.fees ?? []).map((fee) => ({ fee, from: 'home' as const })),
    ...(plan.fees ?? []).map((fee) => ({ fee, from: 'plan' as const })),
    ...propertyFees.map((fee) => ({ fee, from: 'property' as const })),
  ];
  return {
    required: listed.filter((row) => row.fee.is_required === true),
    optional: listed.filter((row) => row.fee.is_required !== true),
    count: listed.length,
  };
}

export interface SharedFees {
  /** Required fees that apply to every listed home: the plan's, the property's, and any home fee every home sends. */
  readonly required: readonly ListedFee[];
  /** At least one listed home sends a required fee the others don't. */
  readonly varies: boolean;
}

/**
 * The required fees a plan's row can show without picking a home. A home fee counts only when every listed home
 * sends the same record, compared field by field as sent. Nothing is parsed or summed.
 */
export function sharedRequiredFees(plan: Floorplan, homes: readonly Unit[], propertyFees: readonly Fee[]): SharedFees {
  const own = homes.map((home) => (home.fees ?? []).filter((fee) => fee.is_required === true));
  const common = (own[0] ?? []).filter((fee) => own.every((fees) => fees.some((other) => feeKey(other) === feeKey(fee))));
  const levels = feesFor(plan, null, propertyFees).required;
  return {
    required: [...common.map((fee) => ({ fee, from: 'home' as const })), ...levels],
    varies: own.some((fees) => fees.length !== common.length),
  };
}

function feeKey(fee: Fee): string {
  return JSON.stringify([fee.name, fee.amount, fee.amount_max, fee.amount_raw, fee.amount_type, fee.timing, fee.is_required, fee.is_refundable]);
}

/** The fee list every plan sends, when there are at least two plans and every list matches. */
function sharedFees(cards: readonly Floorplan[]): readonly Fee[] | null {
  if (cards.length < 2) return null;
  const first = cards[0].fees ?? [];
  if (!first.length) return null;
  const key = (fees: readonly Fee[]) => fees.map(feeKey).join('\n');
  const want = key(first);
  return cards.every((card) => key(card.fees ?? []) === want) ? first : null;
}

function listUnits(row: Floorplan, today: IsoDate): Offer[] {
  const listed: Offer[] = [];
  for (const unit of row.units ?? []) {
    const moveIn = moveInOf(unit, today);
    if (moveIn) listed.push({ unit, moveIn });
  }
  return listed.sort((a, b) => compareListed(a, b));
}

function moveInOf(unit: Unit, today: IsoDate): MoveIn | null {
  if (unit.available !== true) return null;
  const availableOn = isoDate(unit.available_date);
  if (!availableOn) return 'undated';
  if (availableOn > today) return availableOn;
  return 'now';
}

function compareListed(a: Offer, b: Offer): number {
  const bandOf = (moveIn: MoveIn): number => (moveIn === 'now' ? 0 : moveIn === 'undated' ? 2 : 1);
  const band = bandOf(a.moveIn) - bandOf(b.moveIn);
  if (band) return band;
  if (a.moveIn !== 'now' && a.moveIn !== 'undated' && b.moveIn !== 'now' && b.moveIn !== 'undated' && a.moveIn !== b.moveIn) {
    return a.moveIn < b.moveIn ? -1 : 1;
  }
  const rentMin = (unit: Unit) => amount(unit.price_min) ?? amount(unit.price_max) ?? Number.POSITIVE_INFINITY;
  const rent = rentMin(a.unit) - rentMin(b.unit);
  if (rent) return rent;
  const name = (unit: Unit) => text(unit.unit_name) ?? text(unit.unit_id) ?? 'Unit';
  return name(a.unit).localeCompare(name(b.unit));
}

/** Listed units, then the plan price, then every priced unit, then contact. */
function toRent(row: Floorplan, listed: readonly Offer[]): Rent {
  const listedRanges = rangesOf(listed.map((item) => item.unit));
  if (listedRanges.length) return priced(enclose(listedRanges), 'listed');
  const plan = span(amount(row.floorplan_price_min), amount(row.floorplan_price_max));
  if (plan) return priced(plan, 'plan');
  const all = rangesOf(row.units ?? []);
  if (all.length) return priced(enclose(all), 'all-units');
  return { kind: 'contact', label: CONTACT_RENT };
}

function toAvailability(row: Floorplan, listed: readonly Offer[], today: IsoDate): Availability {
  if (listed.length) {
    const now = listed.filter((item) => item.moveIn === 'now').length;
    const undated = listed.filter((item) => item.moveIn === 'undated').length;
    const laterDates = listed.flatMap((item) => (item.moveIn === 'now' || item.moveIn === 'undated' ? [] : [item.moveIn]));
    const later = laterDates.length;
    const next = laterDates.sort()[0] ?? null;
    return { kind: 'listed', now, later, next, label: listedLabel(now, later, undated, next, today) };
  }
  const units = row.units ?? [];
  const reported = count(row.floorplan_available_units);
  if (units.length || reported === 0) return { kind: 'waitlist', label: 'None available' };
  if (reported == null) return { kind: 'unknown', label: 'Ask about availability' };
  return { kind: 'reported', count: reported, label: `${reported} available` };
}

function listedLabel(now: number, later: number, undated: number, next: IsoDate | null, today: IsoDate): string {
  const rest = later + undated;
  if (now === 1 && rest === 0) return 'Available now';
  if (now > 1 && rest === 0) return `${now} available now`;
  if (now > 0) return later === 1 && undated === 0 && next ? `${now} now, 1 on ${formatDay(next, today)}` : `${now} now, ${rest} later`;
  if (later > 0 && next) {
    if (later === 1 && undated === 0) return `Available ${formatDay(next, today)}`;
    return `${rest} available from ${formatDay(next, today)}`;
  }
  return undated === 1 ? '1 available' : `${undated} available`;
}

function toAction(listed: readonly Offer[], availability: Availability, ctx: ModelContext): PlanAction {
  switch (availability.kind) {
    case 'waitlist':
      return contactAction(ctx);
    case 'unknown':
      return contactAction(ctx);
    case 'listed': {
      const href = listed.map((item) => text(item.unit.apply_url)).find((url) => url != null);
      if (href) return applyAction(href, ctx.apply);
      return applyOrContact(ctx);
    }
    case 'reported':
      return applyOrContact(ctx);
    default: {
      const unhandled: never = availability;
      return unhandled;
    }
  }
}

function toDeposit(row: Floorplan, listed: readonly Offer[]): string | null {
  const plan = span(amount(row.floorplan_deposit_min), amount(row.floorplan_deposit_max));
  const unitRanges = listed.flatMap((item) => {
    const range = span(amount(item.unit.deposit_min), amount(item.unit.deposit_max));
    return range ? [range] : [];
  });
  const units = unitRanges.length ? enclose(unitRanges) : null;
  if (plan && units && plan.min === units.min && plan.max === units.max) return `${formatRange(plan)} deposit`;
  if (plan && units) return `${formatRange(units)} deposit by unit, plan lists ${formatRange(plan)}`;
  const only = plan ?? units;
  return only ? `${formatRange(only)} deposit` : null;
}

function toPetTerms(policies: readonly PetPolicy[]): LabeledPet[] {
  return policies.flatMap((policy) => {
    const pet = text(policy.pet_type);
    return pet ? [{ ...policy, label: petLabel(pet, policy) }] : [];
  });
}

function bedCount(row: Floorplan): number | null {
  return parseBedroomCount(row.floorplan_beds_min);
}

/** `planUrls` holds the drawing's and the plan photos' urls, which a home's photos skip. `planTours` holds the plan's own tour hrefs, which a home's tours skip. */
function toListedUnit(item: Offer, today: IsoDate, planUrls: ReadonlySet<string>, planTours: ReadonlySet<string>): ListedUnit {
  const unit = item.unit;
  const range = rangeOf(unit);
  const sqft = amount(unit.sqft);
  return {
    unit: { ...unit, floor: text(unit.floor), apply_url: text(unit.apply_url) },
    moveIn: item.moveIn,
    moveInLabel: item.moveIn === 'now' ? 'Now' : item.moveIn === 'undated' ? 'Ask for date' : formatDay(item.moveIn, today),
    rentLabel: range ? rentLabel(range) : CONTACT_RENT,
    sqftLabel: sqft == null ? null : formatNumber(sqft),
    depositLabel: dollars(span(amount(unit.deposit_min), amount(unit.deposit_max))),
    specials: (unit.special_offers ?? []).filter((offer) => isActive(offer, today)),
    amenities: amenityLines(unit.amenities),
    photos: galleryPhotos(unit.photo_gallery, `${text(unit.unit_name) ?? 'Home'} photo`, new Set(planUrls)),
    tour: toursOn(unit)[0] ?? null,
    tours: distinctTours(toursOn(unit).filter((tour) => !planTours.has(tour.href))),
    location: locationOf(text(unit.building), text(unit.floor)),
  };
}

function locationOf(building: string | null, floor: string | null): string | null {
  /** Some feeds send "Building 2", others "2". */
  const label = building && (/^(building|bldg)\b/i.test(building) ? building : `Building ${building}`);
  if (label && floor) return `${label}, floor ${floor}`;
  if (floor) return `Floor ${floor}`;
  return label;
}

function unitColumns(rows: readonly ListedUnit[]): UnitColumns {
  return {
    sqft: rows.some((row) => row.sqftLabel != null),
    location: rows.some((row) => row.location != null),
    deposit: new Set(rows.map((row) => row.depositLabel)).size > 1,
    special: rows.some((row) => row.specials.length > 0),
    tour: rows.some((row) => row.tour != null),
    apply: rows.some((row) => row.unit.apply_url != null),
  };
}

function hasHomes(card: Floorplan & Display): boolean {
  return card.availability.kind === 'listed' || card.availability.kind === 'reported';
}

/** Every bed count a plan covers: 2 for a 2 bed, 2 and 3 for a "2–3 beds" plan. */
function bedsOf(card: Display): number[] {
  const low = card.beds;
  if (low == null) return [];
  const top = card.bedsMax ?? low;
  return Array.from({ length: Math.min(top - low, 10) + 1 }, (_, step) => low + step);
}

function bedroomFilter(cards: readonly Display[], beds: BedSelection): BedroomFilter | null {
  const counts = new Map<number, number>();
  for (const card of cards) {
    for (const bed of bedsOf(card)) counts.set(bed, (counts.get(bed) ?? 0) + 1);
  }
  if (counts.size < 2) return null;
  const options: BedroomFilter['options'] = [
    { beds: null, label: 'All', count: cards.length },
    ...[...counts.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([bed, bedCount]) => ({ beds: bed, label: bedLabel(bed), count: bedCount })),
  ];
  return { options, selected: beds.selected, select: beds.select };
}

function toGroups(plans: readonly (Floorplan & Display)[]): PlanGroup[] {
  const byBeds = new Map<number | null, (Floorplan & Display)[]>();
  for (const plan of plans) byBeds.set(plan.beds, [...(byBeds.get(plan.beds) ?? []), plan]);
  return [...byBeds.entries()]
    .sort(([a], [b]) => (a ?? Number.POSITIVE_INFINITY) - (b ?? Number.POSITIVE_INFINITY))
    .map(([beds, members]) => ({ beds, label: groupLabel(beds), summary: groupSummary(members), plans: members }));
}

function groupLabel(beds: number | null): string {
  if (beds == null) return 'Other plans';
  if (beds === 0) return 'Studio';
  return `${beds} ${beds === 1 ? 'bedroom' : 'bedrooms'}`;
}

function groupSummary(plans: readonly (Floorplan & Display)[]): string {
  const head = `${plans.length} ${plans.length === 1 ? 'plan' : 'plans'}`;
  const lows = plans.flatMap((plan) => (plan.rent.kind === 'priced' && plan.rent.basis !== 'all-units' ? [plan.rent.range.min] : []));
  return lows.length ? `${head}, from ${formatDollars(Math.min(...lows))}` : head;
}

function distinctTours(tours: readonly Tour[]): Tour[] {
  const seen = new Set<string>();
  return tours.filter((tour) => !seen.has(tour.href) && Boolean(seen.add(tour.href)));
}

/** A gallery's photos with an https image, minus any whose url is in `seen`. Kept urls are added to `seen`. */
function galleryPhotos(gallery: readonly PhotoGallery[] | null | undefined, fallbackAlt: string, seen: Set<string>): PlanPhoto[] {
  return (gallery ?? []).flatMap((photo) => {
    const url = text(photo.image);
    if (!url || !/^https?:\/\//i.test(url) || seen.has(url)) return [];
    seen.add(url);
    const alt = text(photo.alt_text) ?? text(photo.title) ?? fallbackAlt;
    return [{ url, alt, title: text(photo.title), description: text(photo.description), media: photo.image_media ?? null }];
  });
}

/** Titled amenities in feed order, the first of each title kept. Featured follows the property hook's rule. */
function amenityLines(amenities: readonly Amenity[] | null | undefined): AmenityLine[] {
  const seen = new Set<string>();
  return (amenities ?? []).flatMap((amenity) => {
    const title = text(amenity.title);
    if (!title || seen.has(amenityKey(title))) return [];
    seen.add(amenityKey(title));
    return [{ title, description: text(amenity.description), amount: text(amenity.amount), featured: amenity.is_featured === 'true' }];
  });
}

function amenityKey(title: string): string {
  return title.trim().toLowerCase();
}

/**
 * The plan's amenities, then what the selected home adds, then what only other listed homes have. An amenity every
 * listed home sends counts as the plan's when there are at least two homes: Entrata copies apartment amenities onto
 * each unit, and the home group would otherwise repeat the plan. The total across groups does not depend on `selected`.
 */
export function amenityGroups(plan: Pick<Display, 'amenities' | 'listed'>, selected: ListedUnit | null): AmenityGroup[] {
  const homes = plan.listed;
  const own = new Set(plan.amenities.map((line) => amenityKey(line.title)));
  const shared = homes.length >= 2
    ? homes[0].amenities.filter((line) => !own.has(amenityKey(line.title)) && homes.every((home) => home.amenities.some((other) => amenityKey(other.title) === amenityKey(line.title))))
    : [];
  const planItems = [...plan.amenities, ...shared];
  const inPlan = new Set(planItems.map((line) => amenityKey(line.title)));
  const extras = (home: ListedUnit) => home.amenities.filter((line) => !inPlan.has(amenityKey(line.title)));
  const mine = selected ? extras(selected) : [];
  const mineKeys = new Set(mine.map((line) => amenityKey(line.title)));
  const others = new Map<string, AmenityLine & { homes: ListedUnit[] }>();
  for (const home of homes) {
    if (home === selected) continue;
    for (const line of extras(home)) {
      const key = amenityKey(line.title);
      if (mineKeys.has(key)) continue;
      const entry = others.get(key);
      if (entry) entry.homes.push(home);
      else others.set(key, { ...line, homes: [home] });
    }
  }
  const groups: AmenityGroup[] = [];
  if (selected && mine.length) groups.push({ kind: 'home', home: selected, items: featuredFirst(mine) });
  if (planItems.length) groups.push({ kind: 'plan', items: featuredFirst(planItems) });
  if (others.size) groups.push({ kind: 'others', items: featuredFirst([...others.values()]) });
  return groups;
}

function featuredFirst<T extends AmenityLine>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => Number(b.featured) - Number(a.featured));
}

function toursOn(parent: { readonly virtual_tours?: readonly VirtualTour[] | null }): Tour[] {
  return (parent.virtual_tours ?? []).flatMap((tour) => {
    const raw = text(tour.virtual_tour_url);
    if (!raw || !/^https?:\/\//i.test(raw)) return [];
    const href = raw.replace(/^http:\/\//i, 'https://');
    const title = text(tour.virtual_tour_title);
    return [{ ...tour, href, label: title ?? 'Virtual tour' }];
  });
}

function rangesOf(units: readonly Unit[]): Range[] {
  return units.flatMap((unit) => {
    const range = rangeOf(unit);
    return range ? [range] : [];
  });
}

function rangeOf(unit: Unit): Range | null {
  return span(amount(unit.price_min), amount(unit.price_max));
}

function priced(range: Range, basis: 'listed' | 'plan' | 'all-units'): Rent {
  return { kind: 'priced', basis, range, label: rentLabel(range) };
}

function rentLabel(range: Range): string {
  return `${formatRange(range)}/mo`;
}

function sqftOf(row: Floorplan, listed: readonly Offer[]): Range | null {
  const plan = span(amount(row.floorplan_sqft_min), amount(row.floorplan_sqft_max));
  if (plan) return plan;
  const values = listed.flatMap((item) => {
    const sqft = amount(item.unit.sqft);
    return sqft == null ? [] : [sqft];
  });
  if (!values.length) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

function factsLabel(beds: number | null, baths: number | null, sqft: Range | null, bedsMax: number | null, bathsMax: number | null): string {
  const parts: string[] = [];
  if (beds != null && bedsMax != null) parts.push(`${beds === 0 ? 'Studio' : formatNumber(beds)}–${formatNumber(bedsMax)} beds`);
  else if (beds != null) parts.push(bedLabel(beds));
  if (baths != null && bathsMax != null) parts.push(`${formatNumber(baths)}–${formatNumber(bathsMax)} baths`);
  else if (baths != null) parts.push(`${formatNumber(baths)} ${baths === 1 ? 'bath' : 'baths'}`);
  if (sqft) parts.push(`${formatNumberRange(sqft)} sq ft`);
  return parts.join(', ');
}

function petLabel(pet: string, policy: PetPolicy): string {
  if (policy.allowed === false) return `${pet} not allowed`;
  const parts = [policy.allowed === true ? `${pet} allowed` : pet];
  const max = count(policy.max_count);
  const weight = amount(policy.weight_limit);
  if (max != null && max > 0) parts.push(`up to ${max}`);
  if (weight != null) parts.push(`${formatNumber(weight)} lb limit`);
  const monthly = amount(policy.monthly_rent);
  const deposit = amount(policy.deposit);
  const fee = amount(policy.fee);
  if (monthly != null) parts.push(`${formatDollars(monthly)}/mo`);
  if (deposit != null) parts.push(`${formatDollars(deposit)} deposit`);
  if (fee != null) parts.push(`${formatDollars(fee)} fee`);
  return parts.join(', ');
}

function dollars(range: Range | null): string | null {
  return range ? formatRange(range) : null;
}

function contactAction(ctx: ModelContext): PlanAction {
  if (!ctx.contact) return { kind: 'none' };
  return { kind: 'contact', ...ctx.contact };
}

function applyAction(href: string, apply: ApplyLink): PlanAction {
  return { kind: 'apply', href, label: apply.label, newTab: apply.newTab };
}

function applyOrContact(ctx: ModelContext): PlanAction {
  if (ctx.apply.href) return applyAction(ctx.apply.href, ctx.apply);
  return contactAction(ctx);
}

function bedLabel(beds: number): string {
  if (beds === 0) return 'Studio';
  if (beds === 1) return '1 bed';
  return `${beds} beds`;
}

function enclose(ranges: readonly Range[]): Range {
  return {
    min: Math.min(...ranges.map((range) => range.min)),
    max: Math.max(...ranges.map((range) => range.max)),
  };
}
